import { FC, useMemo, useState, useEffect, useRef } from "react";
import { Spin, Card, Table, Tag, Statistic, Row, Col, Divider, Button, Input, InputNumber, DatePicker, message, Select, Radio } from "antd";
import { useDetailBookingStore } from "../detail-booking.store";
import { ServiceType } from "~/utils/constants";
import { capitalize } from "~/utils";
import { calculateServiceCost } from "~/utils/service-cost-calculator";
import { useGetStaffRoleByName, useGetStaffRoles } from "~/services/common";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { addOnServicesApi } from "~/services/add-on-services/add-on-services.api";
import { useGetAdminCharges } from "~/services/admin-charge";
import { bookingService } from "~/services/booking/booking.services";
import { useUsers } from "~/services/user";
import { Role } from "~/utils/constants";
import { TrendingUp, TrendingDown, DollarSign, Users, Truck, Package, Plus, Trash2 } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";

// Format currency helper function
const formatCurrency = (amount: number | undefined): string => {
  if (amount === undefined) return "$0.00";
  return `$${Number(amount ?? 0)
    .toFixed(2)
    .replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;
};

type Props = {};

// Other costs type (expenses like 3rd party shipper, airfreight, etc.)
type OtherCost = {
  id: string;
  description: string;
  notes?: string;
  date?: string;
  amount: number;
};

export const ProfitLoss: FC<Props> = () => {
  const { booking, setBooking } = useDetailBookingStore();
  const queryClient = useQueryClient();
  
  // Other costs state (expenses - behind the scenes, not in estimates)
  const [otherCosts, setOtherCosts] = useState<OtherCost[]>([]);
  const [isSavingOtherCosts, setIsSavingOtherCosts] = useState(false);

  // Commission state (lead/sales commission)
  const [commissionType, setCommissionType] = useState<'profit' | 'revenue'>('profit');
  const [commissionPercentage, setCommissionPercentage] = useState<number>(0);
  const [commissionUserId, setCommissionUserId] = useState<string | null>(null);
  const [isSavingCommission, setIsSavingCommission] = useState(false);

  // Fetch users (admins and staff) for commission assignment
  const { data: usersData, isLoading: loadingUsers } = useUsers({
    roles: [Role.STAFF, Role.ADMIN, Role.SUPER_ADMIN],
    pageSize: 100,
  });
  const users = usersData?.items || [];

  // Load other costs from invoice config
  // Use a ref to track if we're in the middle of a save operation to prevent overwriting local changes
  const isSavingRef = useRef(false);
  
  useEffect(() => {
    // Don't overwrite local state if we're currently saving
    if (isSavingRef.current) {
      return;
    }
    
    if (booking?.invoiceConfig?.otherCosts && Array.isArray(booking.invoiceConfig.otherCosts)) {
      const configOtherCosts = booking.invoiceConfig.otherCosts;
      setOtherCosts(prev => {
        // Only update if the arrays are actually different (by ID comparison for better accuracy)
        const prevIds = prev.map(c => c.id).sort().join(',');
        const configIds = configOtherCosts.map((c: any) => c.id).sort().join(',');
        if (prevIds !== configIds) {
          return configOtherCosts;
        }
        return prev;
      });
    } else if (booking?.invoiceConfig && !booking.invoiceConfig.otherCosts) {
      // Explicitly handle case where otherCosts doesn't exist in config
      setOtherCosts([]);
    }
  }, [booking?.invoiceConfig?.otherCosts, booking?.id]);

  // Load commission settings from invoice config
  useEffect(() => {
    if (booking?.invoiceConfig?.commission) {
      setCommissionType(booking.invoiceConfig.commission.type || 'profit');
      setCommissionPercentage(booking.invoiceConfig.commission.percentage || 0);
      setCommissionUserId(booking.invoiceConfig.commission.userId || null);
    } else {
      setCommissionType('profit');
      setCommissionPercentage(0);
      setCommissionUserId(null);
    }
  }, [booking?.invoiceConfig?.commission]);

  // Get Art Handler role data for staff rates (for revenue calculation)
  const { data: artHandlerRole, isLoading: isLoadingRole } = useGetStaffRoleByName("Art Handler", booking?.clientId);

  // Get all staff roles to access baseHourlyCost (our actual costs)
  const { data: staffRolesData, isLoading: isLoadingStaffRoles } = useGetStaffRoles();
  const staffRoles = useMemo(() => {
    if (!staffRolesData?.items) return [];
    return staffRolesData.items;
  }, [staffRolesData]);

  // Get add-on services data for pricing
  const { data: addOnServices, isLoading: isLoadingAddOns } = useQuery({
    queryKey: ["add-on-services"],
    queryFn: () => addOnServicesApi.getAll(),
  });

  // Get admin charges data for calculating revenue
  const { data: adminChargesData } = useGetAdminCharges({ params: {} });
  const adminCharges = useMemo(() => adminChargesData?.items || [], [adminChargesData]);

  const isLoading = isLoadingRole || isLoadingAddOns || isLoadingStaffRoles;

  // Get services from booking
  const services = useMemo(() => {
    if (!booking?.serviceTicket) return [];
    return booking.serviceTicket.sort((a, b) => {
      return new Date(a.serviceDatetime).getTime() - new Date(b.serviceDatetime).getTime();
    });
  }, [booking]);

  // Calculate costs for all services
  const profitLossData = useMemo(() => {
    if (!services.length || isLoading) {
      return {
        totalStaffCost: 0,
        totalVesselCost: 0,
        totalMaterialsCost: 0,
        totalRevenue: 0,
        totalExpenses: 0,
        profit: 0,
        profitMargin: 0,
        serviceBreakdown: [],
        staffBreakdown: [],
        vesselBreakdown: [],
        materialsBreakdown: [],
      };
    }

    // Get service overrides from invoice config if available
    const serviceOverrides = new Map();
    if (booking?.invoiceConfig?.serviceOverrides) {
      booking.invoiceConfig.serviceOverrides.forEach((override: any) => {
        if (override.serviceId) {
          serviceOverrides.set(override.serviceId, override);
        }
      });
    }

    let totalStaffCost = 0;
    let totalVesselCost = 0;
    let totalMaterialsCost = 0;
    let totalRevenue = 0;

    // Track breakdowns
    const serviceBreakdown: any[] = [];
    const staffBreakdownMap = new Map<string, { name: string; hours: number; cost: number; rate: number }>();
    const vesselBreakdownMap = new Map<string, { name: string; hours: number; cost: number; rate: number }>();
    const materialsBreakdown: any[] = [];

    services.forEach((service) => {
      const costData = calculateServiceCost({
        service,
        artHandlerRole: artHandlerRole || null,
        addOnServices: addOnServices || [],
        serviceOverrides,
      });

      // Get revenue (what we charge the client)
      const override = serviceOverrides.get(service.id);
      let serviceRevenue = costData.total;
      
      // If there's a custom total override, use that as revenue
      if (override?.customTotal !== undefined && override.customTotal > 0) {
        serviceRevenue = override.customTotal;
      }

      // Calculate actual costs (what it costs us - NOT what we charge)
      // Get travel and duration hours for cost calculation
      const travelHours = costData.breakdown.travelHours || 0;
      const durationHours = costData.breakdown.durationHours || 0;
      const totalHours = travelHours + durationHours;
      const staffCount = costData.breakdown.staffCount || service.staffs?.length || 1;

      // Calculate actual staff cost using baseHourlyCost (our cost, not what we charge)
      let actualStaffCost = 0;

      // Check if this is an EXPO service with positions
      if (service.isExpoDay && service.positions && service.positions.length > 0) {
        // EXPO Mode: Calculate labor cost from positions using baseHourlyCost (our cost)
        service.positions.forEach((pos: any) => {
          const posQty = Number(pos.quantity) || 1;
          const posDuration = Number(pos.duration) || 0;
          // Find the staff role to get our actual cost
          const positionId = pos.positionId || pos.position?.id;
          const staffRole = staffRoles.find((r: any) => r.id === positionId);
          // Use baseHourlyCost (our cost) or fall back to position's stored rate
          const actualHourlyCost = staffRole?.baseHourlyCost || staffRole?.hourlyRate || Number(pos.hourlyRate) || 35;
          actualStaffCost += posQty * posDuration * actualHourlyCost;
        });
      } else if (service.staffs && service.staffs.length > 0) {
        // Regular service: Each staff member works the full hours
        service.staffs.forEach((staff: any) => {
          const staffRoleId = staff.staffRole?.id || staff.staffRoleId;
          const staffRole = staffRoles.find((r: any) => r.id === staffRoleId);
          // Use baseHourlyCost (our cost) or default to $35/hr if not available
          const actualHourlyCost = staffRole?.baseHourlyCost || staffRole?.hourlyRate || 35;
          // Each staff member works the full totalHours, not divided
          actualStaffCost += totalHours * actualHourlyCost;
        });
      } else {
        // If no staff assigned, use default art handler base cost
        const defaultArtHandlerRole = staffRoles.find((r: any) => r.name === "Art Handler");
        const actualHourlyCost = defaultArtHandlerRole?.baseHourlyCost || defaultArtHandlerRole?.hourlyRate || 35;
        actualStaffCost = staffCount * actualHourlyCost * totalHours;
      }

      // Calculate actual vessel cost using directCost (our cost, not what we charge)
      let actualVesselCost = 0;
      if (service.vessel && totalHours > 0) {
        // Use directCost (our cost) or default to $50/hr if not available
        const vesselDirectCost = service.vessel.directCost || 50;
        actualVesselCost = vesselDirectCost * totalHours;
      }

      // Materials cost - use the cost field from supply items (what we paid, not what we charge)
      let actualMaterialsCost = 0;
      if (service.supplyItems && service.supplyItems.length > 0) {
        service.supplyItems.forEach((supplyItem: any) => {
          // Use cost from supply (what we paid) or unitCost if cost not available
          const supplyCost = supplyItem.supply?.cost || supplyItem.unitCost || 0;
          const quantity = supplyItem.quantity || 1;
          actualMaterialsCost += supplyCost * quantity;
        });
      }

      totalStaffCost += actualStaffCost;
      totalVesselCost += actualVesselCost;
      totalMaterialsCost += actualMaterialsCost;
      totalRevenue += serviceRevenue;

      // Service breakdown
      serviceBreakdown.push({
        key: service.id,
        serviceType: service.serviceType,
        serial: service.serial,
        date: service.serviceDatetime,
        staffCost: actualStaffCost,
        vesselCost: actualVesselCost,
        materialsCost: actualMaterialsCost,
        totalCost: actualStaffCost + actualVesselCost + actualMaterialsCost,
        revenue: serviceRevenue,
        profit: serviceRevenue - (actualStaffCost + actualVesselCost + actualMaterialsCost),
      });

      // Staff breakdown - aggregate by staff role/position (using actual costs)
      if (service.isExpoDay && service.positions && service.positions.length > 0) {
        // EXPO Mode: Break down by position from expo positions
        service.positions.forEach((pos: any) => {
          const positionId = pos.positionId || pos.position?.id;
          const staffRole = staffRoles.find((r: any) => r.id === positionId);
          const positionName = pos.position?.name || staffRole?.name || "Unknown Position";
          const posQty = Number(pos.quantity) || 1;
          const posDuration = Number(pos.duration) || 0;
          // Use baseHourlyCost (our cost) or fall back to position's stored rate
          const actualHourlyCost = staffRole?.baseHourlyCost || staffRole?.hourlyRate || Number(pos.hourlyRate) || 35;
          const staffHours = posQty * posDuration;
          const staffCostForThis = staffHours * actualHourlyCost;

          const existing = staffBreakdownMap.get(positionName);
          if (existing) {
            existing.hours += staffHours;
            existing.cost += staffCostForThis;
            if (actualHourlyCost !== existing.rate) {
              existing.rate = actualHourlyCost;
            }
          } else {
            staffBreakdownMap.set(positionName, {
              name: positionName,
              hours: staffHours,
              cost: staffCostForThis,
              rate: actualHourlyCost,
            });
          }
        });
      } else if (service.staffs && service.staffs.length > 0) {
        // Regular service: Each staff member works the full hours
        service.staffs.forEach((staff: any) => {
          const position = staff.position || staff.staffRole?.name || "Art Handler";
          const staffRoleId = staff.staffRole?.id || staff.staffRoleId;
          const staffRole = staffRoles.find((r: any) => r.id === staffRoleId);
          // Use baseHourlyCost (our cost) or default to $35/hr
          const actualHourlyCost = staffRole?.baseHourlyCost || staffRole?.hourlyRate || 35;
          // Each staff member works the full totalHours, not divided
          const staffHours = totalHours;
          const staffCostForThis = staffHours * actualHourlyCost;

          const existing = staffBreakdownMap.get(position);
          if (existing) {
            existing.hours += staffHours;
            existing.cost += staffCostForThis;
            // Update rate if this one is different (should be same for same position)
            if (actualHourlyCost !== existing.rate) {
              existing.rate = actualHourlyCost; // Use the most recent rate
            }
          } else {
            staffBreakdownMap.set(position, {
              name: position,
              hours: staffHours,
              cost: staffCostForThis,
              rate: actualHourlyCost,
            });
          }
        });
      } else {
        // If no staff assigned, use default calculation
        const position = "Art Handler";
        const defaultArtHandlerRole = staffRoles.find((r: any) => r.name === "Art Handler");
        const actualHourlyCost = defaultArtHandlerRole?.baseHourlyCost || defaultArtHandlerRole?.hourlyRate || 35;
        // Total staff-hours = number of staff × hours each
        const staffHours = totalHours * staffCount;
        const staffCostForThis = staffHours * actualHourlyCost;

        const existing = staffBreakdownMap.get(position);
        if (existing) {
          existing.hours += staffHours;
          existing.cost += staffCostForThis;
        } else {
          staffBreakdownMap.set(position, {
            name: position,
            hours: staffHours,
            cost: staffCostForThis,
            rate: actualHourlyCost,
          });
        }
      }

      // Vessel breakdown (using actual costs)
      if (service.vessel && totalHours > 0) {
        const vesselName = service.vessel.name || "Unknown Vessel";
        // Use directCost (our cost) or default to $50/hr
        const vesselDirectCost = service.vessel.directCost || 50;
        const vesselCostForThis = vesselDirectCost * totalHours;

        const existing = vesselBreakdownMap.get(service.vessel.id);
        if (existing) {
          existing.hours += totalHours;
          existing.cost += vesselCostForThis;
        } else {
          vesselBreakdownMap.set(service.vessel.id, {
            name: vesselName,
            hours: totalHours,
            cost: vesselCostForThis,
            rate: vesselDirectCost,
          });
        }
      }

      // Materials breakdown (using actual costs - what we paid)
      if (service.supplyItems && service.supplyItems.length > 0) {
        service.supplyItems.forEach((supplyItem: any) => {
          const supplyName = supplyItem.supply?.name || "Unknown Supply";
          const quantity = supplyItem.quantity || 1;
          // Use cost from supply (what we paid) or unitCost if cost not available
          const supplyCost = supplyItem.supply?.cost || supplyItem.unitCost || 0;
          const totalCost = quantity * supplyCost;

          materialsBreakdown.push({
            key: `${service.id}-${supplyItem.id}`,
            serviceSerial: service.serial,
            supplyName,
            quantity,
            unitCost: supplyCost,
            totalCost,
          });
        });
      }
    });

    // Calculate admin charges revenue (from invoice config)
    let adminChargesRevenue = 0;
    if (booking?.invoiceConfig?.adminCharges && adminCharges.length > 0) {
      adminChargesRevenue = booking.invoiceConfig.adminCharges.reduce((total: number, charge: any) => {
        const chargeInfo = adminCharges.find((c: any) => c.id === charge.chargeId);
        if (chargeInfo && chargeInfo.rate) {
          return total + (chargeInfo.rate * (charge.quantity || 1));
        }
        return total;
      }, 0);
    }

    // Calculate additional charges revenue (from invoice config)
    let additionalChargesRevenue = 0;
    if (booking?.invoiceConfig?.additionalCharges && Array.isArray(booking.invoiceConfig.additionalCharges)) {
      additionalChargesRevenue = booking.invoiceConfig.additionalCharges.reduce((total: number, charge: any) => {
        const chargeAmount = (Number(charge?.quantity) || 1) * (Number(charge?.rate) || 0);
        return total + chargeAmount;
      }, 0);
    }

    // Add admin charges and additional charges to total revenue
    const totalRevenueWithCharges = totalRevenue + adminChargesRevenue + additionalChargesRevenue;

    // Calculate other costs (expenses like 3rd party shipper, airfreight, etc.)
    let otherCostsTotal = 0;
    if (booking?.invoiceConfig?.otherCosts && Array.isArray(booking.invoiceConfig.otherCosts)) {
      otherCostsTotal = booking.invoiceConfig.otherCosts.reduce((total: number, cost: any) => {
        return total + (Number(cost?.amount) || 0);
      }, 0);
    }

    // Calculate commission (lead/sales commission)
    // Note: Commission is calculated from saved config, but we also need to calculate it from current state for display
    let commissionAmount = 0;
    const expensesBeforeCommission = totalStaffCost + totalVesselCost + totalMaterialsCost + otherCostsTotal;
    const profitBeforeCommission = totalRevenueWithCharges - expensesBeforeCommission;
    
    if (booking?.invoiceConfig?.commission) {
      const commission = booking.invoiceConfig.commission;
      if (commission.type === 'profit') {
        commissionAmount = profitBeforeCommission * ((commission.percentage || 0) / 100);
      } else if (commission.type === 'revenue') {
        commissionAmount = totalRevenueWithCharges * ((commission.percentage || 0) / 100);
      }
    }

    const totalExpenses = expensesBeforeCommission + commissionAmount;
    const profit = totalRevenueWithCharges - totalExpenses;
    const profitMargin = totalRevenueWithCharges > 0 ? (profit / totalRevenueWithCharges) * 100 : 0;

    return {
      totalStaffCost,
      totalVesselCost,
      totalMaterialsCost,
      otherCostsTotal,
      commissionAmount,
      totalRevenue: totalRevenueWithCharges,
      adminChargesRevenue,
      additionalChargesRevenue,
      totalExpenses,
      profit,
      profitMargin,
      serviceBreakdown,
      staffBreakdown: Array.from(staffBreakdownMap.values()),
      vesselBreakdown: Array.from(vesselBreakdownMap.values()),
      materialsBreakdown,
    };
  }, [services, artHandlerRole, addOnServices, booking?.invoiceConfig, staffRoles, adminCharges, isLoading]);

  // Save other costs to invoice config
  const handleSaveOtherCosts = async () => {
    if (!booking?.id) {
      message.error('No booking found to save costs');
      return;
    }

    setIsSavingOtherCosts(true);
    isSavingRef.current = true;
    try {
      // Get current invoice config and merge in other costs
      // Important: Explicitly set otherCosts to ensure deletions are persisted
      const currentConfig = booking.invoiceConfig || {};
      const configData = {
        ...currentConfig,
        otherCosts: otherCosts.length > 0 ? otherCosts : [], // Explicitly set empty array if no costs
      };

      await bookingService.updateBookingInvoiceConfig(booking.id, configData);

      // Invalidate and refetch the booking to get the latest data
      await queryClient.invalidateQueries({ queryKey: ['bookings', booking.id] });
      const updatedBooking = await bookingService.getBooking(booking.id);
      setBooking(updatedBooking);

      message.success('Other costs saved successfully');
    } catch (error: any) {
      console.error('Error saving other costs:', error);
      message.error(error.message || 'Failed to save other costs');
    } finally {
      setIsSavingOtherCosts(false);
      // Reset the ref after a short delay to allow state updates to complete
      setTimeout(() => {
        isSavingRef.current = false;
      }, 500);
    }
  };

  // Save commission settings to invoice config
  const handleSaveCommission = async () => {
    if (!booking?.id) {
      message.error('No booking found to save commission');
      return;
    }

    setIsSavingCommission(true);
    try {
      // Get current invoice config and merge in commission
      const currentConfig = booking.invoiceConfig || {};
      const configData = {
        ...currentConfig,
        commission: {
          type: commissionType,
          percentage: commissionPercentage,
          userId: commissionUserId || null,
        },
      };

      await bookingService.updateBookingInvoiceConfig(booking.id, configData);

      // Invalidate and refetch the booking to get the latest data
      await queryClient.invalidateQueries({ queryKey: ['bookings', booking.id] });
      const updatedBooking = await bookingService.getBooking(booking.id);
      setBooking(updatedBooking);

      message.success('Commission settings saved successfully');
    } catch (error: any) {
      console.error('Error saving commission:', error);
      message.error(error.message || 'Failed to save commission');
    } finally {
      setIsSavingCommission(false);
    }
  };

  // Early return if booking is not loaded yet
  if (!booking) {
    return <Spin size="large" className="flex justify-center items-center min-h-[400px]" />;
  }

  // Table columns for service breakdown
  const serviceColumns = [
    {
      title: "Service",
      dataIndex: "serviceType",
      key: "serviceType",
      render: (type: ServiceType) => (
        <Tag color="blue">{capitalize(type).replace(" Service", "")}</Tag>
      ),
    },
    {
      title: "Serial",
      dataIndex: "serial",
      key: "serial",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date: Date) => dayjs(date).format("MMM D, YYYY"),
    },
    {
      title: "Staff Cost",
      dataIndex: "staffCost",
      key: "staffCost",
      align: "right" as const,
      render: (cost: number) => formatCurrency(cost),
    },
    {
      title: "Vessel Cost",
      dataIndex: "vesselCost",
      key: "vesselCost",
      align: "right" as const,
      render: (cost: number) => formatCurrency(cost),
    },
    {
      title: "Materials Cost",
      dataIndex: "materialsCost",
      key: "materialsCost",
      align: "right" as const,
      render: (cost: number) => formatCurrency(cost),
    },
    {
      title: "Total Cost",
      dataIndex: "totalCost",
      key: "totalCost",
      align: "right" as const,
      render: (cost: number) => <strong>{formatCurrency(cost)}</strong>,
    },
    {
      title: "Revenue",
      dataIndex: "revenue",
      key: "revenue",
      align: "right" as const,
      render: (revenue: number) => <strong>{formatCurrency(revenue)}</strong>,
    },
    {
      title: "Profit/Loss",
      dataIndex: "profit",
      key: "profit",
      align: "right" as const,
      render: (profit: number) => (
        <span className={profit >= 0 ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>
          {formatCurrency(profit)}
        </span>
      ),
    },
  ];

  // Table columns for staff breakdown
  const staffColumns = [
    {
      title: "Position",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Hours",
      dataIndex: "hours",
      key: "hours",
      align: "right" as const,
      render: (hours: number) => hours.toFixed(2),
    },
    {
      title: "Rate/Hour",
      dataIndex: "rate",
      key: "rate",
      align: "right" as const,
      render: (rate: number) => formatCurrency(rate),
    },
    {
      title: "Total Cost",
      dataIndex: "cost",
      key: "cost",
      align: "right" as const,
      render: (cost: number) => <strong>{formatCurrency(cost)}</strong>,
    },
  ];

  // Table columns for vessel breakdown
  const vesselColumns = [
    {
      title: "Vessel",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Hours",
      dataIndex: "hours",
      key: "hours",
      align: "right" as const,
      render: (hours: number) => hours.toFixed(2),
    },
    {
      title: "Rate/Hour",
      dataIndex: "rate",
      key: "rate",
      align: "right" as const,
      render: (rate: number) => formatCurrency(rate),
    },
    {
      title: "Total Cost",
      dataIndex: "cost",
      key: "cost",
      align: "right" as const,
      render: (cost: number) => <strong>{formatCurrency(cost)}</strong>,
    },
  ];

  // Table columns for materials breakdown
  const materialsColumns = [
    {
      title: "Service",
      dataIndex: "serviceSerial",
      key: "serviceSerial",
    },
    {
      title: "Material",
      dataIndex: "supplyName",
      key: "supplyName",
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      align: "right" as const,
    },
    {
      title: "Unit Cost",
      dataIndex: "unitCost",
      key: "unitCost",
      align: "right" as const,
      render: (cost: number) => formatCurrency(cost),
    },
    {
      title: "Total Cost",
      dataIndex: "totalCost",
      key: "totalCost",
      align: "right" as const,
      render: (cost: number) => <strong>{formatCurrency(cost)}</strong>,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium">Profit/Loss Analysis</h3>
      </div>

      {/* Service-by-Service Breakdown - Moved to top */}
      <Card title="Service-by-Service Profit/Loss" className="shadow-sm">
        <Table
          columns={serviceColumns}
          dataSource={profitLossData.serviceBreakdown}
          pagination={profitLossData.serviceBreakdown.length > 10}
          size="small"
          summary={(pageData) => {
            const totalCost = pageData.reduce((sum, record) => sum + record.totalCost, 0);
            const totalRevenue = pageData.reduce((sum, record) => sum + record.revenue, 0);
            const otherCostsTotal = otherCosts.reduce((sum, cost) => sum + (cost.amount || 0), 0);
            const totalCostWithOther = totalCost + otherCostsTotal;
            const totalProfit = totalRevenue - totalCostWithOther;
            
            return (
              <Table.Summary fixed>
                {/* Other Costs Input Rows */}
                <Table.Summary.Row className="bg-orange-50">
                  <Table.Summary.Cell index={0} colSpan={7}>
                    <div className="py-2">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-700">Adjustments (+ expense, − revenue):</span>
                          <Button
                            type="link"
                            size="small"
                            icon={<Plus size={12} />}
                            onClick={() => {
                              const newCost: OtherCost = {
                                id: uuidv4(),
                                description: '',
                                date: dayjs().format('YYYY-MM-DD'),
                                amount: 0,
                              };
                              setOtherCosts([...otherCosts, newCost]);
                            }}
                            className="h-6 text-xs"
                          >
                            Add Cost
                          </Button>
                          {otherCosts.length > 0 && (
                            <Button
                              type="link"
                              size="small"
                              onClick={handleSaveOtherCosts}
                              loading={isSavingOtherCosts}
                              className="h-6 text-xs"
                            >
                              Save
                            </Button>
                          )}
                        </div>
                        <div className="text-sm font-semibold text-orange-700">
                          Total: {formatCurrency(otherCostsTotal)}
                        </div>
                      </div>
                      {otherCosts.length > 0 && (
                        <div className="space-y-2">
                          {otherCosts.map((cost, index) => (
                            <div key={cost.id} className="flex items-center gap-2 bg-white p-2 rounded border">
                              <Input
                                placeholder="Description (e.g., Airfreight, 3PL, or negative for addl revenue)"
                                value={cost.description}
                                onChange={(e) => {
                                  const updated = [...otherCosts];
                                  updated[index] = { ...cost, description: e.target.value };
                                  setOtherCosts(updated);
                                }}
                                className="flex-1 text-xs"
                                size="small"
                              />
                              <DatePicker
                                value={cost.date ? dayjs(cost.date) : null}
                                onChange={(date) => {
                                  const updated = [...otherCosts];
                                  updated[index] = { ...cost, date: date?.format('YYYY-MM-DD') };
                                  setOtherCosts(updated);
                                }}
                                placeholder="Date"
                                size="small"
                                format="MM/DD/YY"
                                className="w-24"
                              />
                              <InputNumber
                                step={0.01}
                                value={cost.amount}
                                onChange={(value) => {
                                  const updated = [...otherCosts];
                                  updated[index] = { ...cost, amount: value ?? 0 };
                                  setOtherCosts(updated);
                                }}
                                placeholder="Amount (neg = revenue)"
                                prefix="$"
                                size="small"
                                className="w-32"
                              />
                              <button
                                onClick={() => {
                                  const updated = otherCosts.filter((_, i) => i !== index);
                                  setOtherCosts(updated);
                                  // Auto-save when deleting to ensure persistence
                                  if (booking?.id) {
                                    isSavingRef.current = true;
                                    setTimeout(async () => {
                                      try {
                                        const currentConfig = booking.invoiceConfig || {};
                                        const configData = {
                                          ...currentConfig,
                                          otherCosts: updated.length > 0 ? updated : [], // Explicitly set empty array
                                        };
                                        await bookingService.updateBookingInvoiceConfig(booking.id, configData);
                                        await queryClient.invalidateQueries({ queryKey: ['bookings', booking.id] });
                                        const updatedBooking = await bookingService.getBooking(booking.id);
                                        setBooking(updatedBooking);
                                      } catch (error) {
                                        console.error('Error auto-saving after delete:', error);
                                      } finally {
                                        setTimeout(() => {
                                          isSavingRef.current = false;
                                        }, 500);
                                      }
                                    }, 100);
                                  }
                                }}
                                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
                
                {/* Totals Row */}
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <strong>Totals</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <strong>{formatCurrency(pageData.reduce((sum, r) => sum + r.staffCost, 0))}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <strong>{formatCurrency(pageData.reduce((sum, r) => sum + r.vesselCost, 0))}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <strong>{formatCurrency(pageData.reduce((sum, r) => sum + r.materialsCost, 0))}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    <strong>{formatCurrency(totalCostWithOther)}</strong>
                    {otherCostsTotal > 0 && (
                      <div className="text-xs text-orange-600">
                        (incl. {formatCurrency(otherCostsTotal)} other)
                      </div>
                    )}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    <strong>{formatCurrency(totalRevenue)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    <span className={totalProfit >= 0 ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>
                      {formatCurrency(totalProfit)}
                    </span>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
        
        {/* Commission Section - Below Totals */}
        <div className="mt-6 pt-4 border-t">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-1">Lead/Sales Commission</h4>
                <p className="text-xs text-gray-600">Track commission as % of profit or revenue and assign to admin/staff</p>
              </div>
              {(commissionPercentage > 0 || commissionUserId || (booking?.invoiceConfig?.commission?.percentage || 0) > 0 || booking?.invoiceConfig?.commission?.userId) && (
                <Button
                  type="primary"
                  size="small"
                  onClick={handleSaveCommission}
                  loading={isSavingCommission}
                >
                  Save
                </Button>
              )}
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-4 flex-wrap">
                <Radio.Group
                  value={commissionType}
                  onChange={(e) => setCommissionType(e.target.value)}
                  size="small"
                >
                  <Radio value="profit">% of Profit</Radio>
                  <Radio value="revenue">% of Revenue</Radio>
                </Radio.Group>
                <div className="flex items-center gap-2">
                  <InputNumber
                    min={0}
                    max={100}
                    step={0.1}
                    value={commissionPercentage}
                    onChange={(value) => setCommissionPercentage(value || 0)}
                    placeholder="0"
                    suffix="%"
                    size="small"
                    className="w-24"
                  />
                  {commissionPercentage > 0 && (
                    <span className="text-sm text-gray-600">
                      = {formatCurrency(
                        commissionType === 'profit' 
                          ? (profitLossData.totalRevenue - profitLossData.totalStaffCost - profitLossData.totalVesselCost - profitLossData.totalMaterialsCost - profitLossData.otherCostsTotal) * (commissionPercentage / 100)
                          : profitLossData.totalRevenue * (commissionPercentage / 100)
                      )}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700 min-w-[100px]">Assign to:</span>
                <Select
                  value={commissionUserId}
                  onChange={(value) => setCommissionUserId(value)}
                  placeholder="Select admin or staff"
                  allowClear
                  showSearch
                  size="small"
                  className="flex-1 max-w-xs"
                  loading={loadingUsers}
                  filterOption={(input, option) => {
                    const label = option?.label || '';
                    return String(label).toLowerCase().includes(input.toLowerCase());
                  }}
                  options={users.map((user: any) => ({
                    value: user.id,
                    label: `${user.firstName} ${user.lastName}${user.username ? ` (@${user.username})` : ''} - ${user.role}`,
                  }))}
                />
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Summary Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Revenue"
              value={profitLossData.totalRevenue}
              prefix={<DollarSign className="text-green-600" size={20} />}
              precision={2}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Expenses"
              value={profitLossData.totalExpenses}
              prefix={<DollarSign className="text-red-600" size={20} />}
              precision={2}
              valueStyle={{ color: "#cf1322" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Profit/Loss"
              value={profitLossData.profit}
              prefix={profitLossData.profit >= 0 ? <TrendingUp className="text-green-600" size={20} /> : <TrendingDown className="text-red-600" size={20} />}
              precision={2}
              valueStyle={{ color: profitLossData.profit >= 0 ? "#3f8600" : "#cf1322" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Profit Margin"
              value={profitLossData.profitMargin}
              suffix="%"
              precision={2}
              valueStyle={{ color: profitLossData.profitMargin >= 0 ? "#3f8600" : "#cf1322" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Revenue Breakdown - Show admin charges and additional charges if present */}
      {(profitLossData.adminChargesRevenue > 0 || profitLossData.additionalChargesRevenue > 0) && (
        <Card title="Revenue Breakdown" className="shadow-sm">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Card size="small" className="text-center bg-green-50">
                <div className="text-lg font-semibold text-gray-600 mb-1">Services Revenue</div>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(profitLossData.totalRevenue - profitLossData.adminChargesRevenue - profitLossData.additionalChargesRevenue)}
                </div>
              </Card>
            </Col>
            {profitLossData.adminChargesRevenue > 0 && (
              <Col xs={24} sm={6}>
                <Card size="small" className="text-center bg-blue-50">
                  <div className="text-lg font-semibold text-gray-600 mb-1">Admin Charges</div>
                  <div className="text-2xl font-bold text-blue-600">
                    {formatCurrency(profitLossData.adminChargesRevenue)}
                  </div>
                </Card>
              </Col>
            )}
            {profitLossData.additionalChargesRevenue > 0 && (
              <Col xs={24} sm={6}>
                <Card size="small" className="text-center bg-purple-50">
                  <div className="text-lg font-semibold text-gray-600 mb-1">Additional Charges</div>
                  <div className="text-2xl font-bold text-purple-600">
                    {formatCurrency(profitLossData.additionalChargesRevenue)}
                  </div>
                </Card>
              </Col>
            )}
          </Row>
        </Card>
      )}

      {/* Expense Breakdown */}
      <Card title="Expense Breakdown" className="shadow-sm">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={6}>
            <Card size="small" className="text-center">
              <Users className="mx-auto mb-2 text-blue-600" size={32} />
              <div className="text-2xl font-bold">{formatCurrency(profitLossData.totalStaffCost)}</div>
              <div className="text-gray-600 text-sm">Staff Costs</div>
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card size="small" className="text-center">
              <Truck className="mx-auto mb-2 text-purple-600" size={32} />
              <div className="text-2xl font-bold">{formatCurrency(profitLossData.totalVesselCost)}</div>
              <div className="text-gray-600 text-sm">Vessel Costs</div>
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card size="small" className="text-center">
              <Package className="mx-auto mb-2 text-orange-600" size={32} />
              <div className="text-2xl font-bold">{formatCurrency(profitLossData.totalMaterialsCost)}</div>
              <div className="text-gray-600 text-sm">Materials Costs</div>
            </Card>
          </Col>
          {profitLossData.otherCostsTotal > 0 && (
            <Col xs={24} sm={6}>
              <Card size="small" className="text-center bg-orange-50">
                <DollarSign className="mx-auto mb-2 text-orange-600" size={32} />
                <div className="text-2xl font-bold text-orange-600">{formatCurrency(profitLossData.otherCostsTotal)}</div>
                <div className="text-gray-600 text-sm">Other Costs</div>
              </Card>
            </Col>
          )}
        </Row>
      </Card>

      {/* Staff Breakdown */}
      {profitLossData.staffBreakdown.length > 0 && (
        <Card title="Staff Cost Breakdown" className="shadow-sm">
          <Table
            columns={staffColumns}
            dataSource={profitLossData.staffBreakdown}
            pagination={false}
            size="small"
            summary={(pageData) => {
              const total = pageData.reduce((sum, record) => sum + record.cost, 0);
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <strong>Total Staff Costs</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <strong>{formatCurrency(total)}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Card>
      )}

      {/* Vessel Breakdown */}
      {profitLossData.vesselBreakdown.length > 0 && (
        <Card title="Vessel Cost Breakdown" className="shadow-sm">
          <Table
            columns={vesselColumns}
            dataSource={profitLossData.vesselBreakdown}
            pagination={false}
            size="small"
            summary={(pageData) => {
              const total = pageData.reduce((sum, record) => sum + record.cost, 0);
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <strong>Total Vessel Costs</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <strong>{formatCurrency(total)}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Card>
      )}

      {/* Materials Breakdown */}
      {profitLossData.materialsBreakdown.length > 0 && (
        <Card title="Materials Cost Breakdown" className="shadow-sm">
          <Table
            columns={materialsColumns}
            dataSource={profitLossData.materialsBreakdown}
            pagination={false}
            size="small"
            summary={(pageData) => {
              const total = pageData.reduce((sum, record) => sum + record.totalCost, 0);
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={4}>
                      <strong>Total Materials Costs</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <strong>{formatCurrency(total)}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Card>
      )}
    </div>
  );
};

